"use client";

import { Briefcase, Zap, ChartBar as BarChart3, Flame, ChevronRight, CircleCheck as CheckCircle2, ArrowUpRight, SlidersHorizontal as Sliders, TrendingUp, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurStrategicPillars() {
  const {
    ref: row1Ref,
    isVisible: row1Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: row2Ref,
    isVisible: row2Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const serviceLinks = [{
    title: "Freelance Hotel Revenue Manager",
    path: "/ServicesPage"
  }, {
    title: "OTA Listing & Optimization",
    path: "/ServicesPage"
  }, {
    title: "Dynamic Pricing & Forecasting",
    path: "/ServicesPage"
  }, {
    title: "Hotel Revenue Audit",
    path: "/ServicesPage"
  }, {
    title: "Hotel Tech Stack Consulting",
    path: "/ServicesPage"
  }, {
    title: "Direct Booking Growth",
    path: "/ServicesPage"
  }];
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Hotel Revenue Management Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "HotelRevBaBa"
    },
    "aggregateOffer": serviceLinks.map(link => ({
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": link.title,
        "url": `https://hotelrevbaba.com${link.path}`
      }
    }))
  };
  const stats = [{
    number: "24/7",
    unit: "",
    label: "Market & Rate Monitoring"
  }, {
    number: "15–25%",
    unit: "",
    label: "Net Revenue Improvement"
  }, {
    number: "700+",
    unit: "",
    label: "Hotels Managed"
  }, {
    number: "4",
    unit: "Pillars",
    label: "Core Services"
  }];
  return <section data-section-id="153" id="our-strategic-pillars" className="bg-[hsl(0,0%,98%)] text-[#0B1528] relative overflow-hidden py-20 md:py-28 lg:py-32 border-b border-slate-200">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(serviceSchema)
    }} />
      
      {/* Background Ambience */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,hsla(43,46%,55%,0.08),transparent_75%)]" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        {/* Breadcrumb & Section Header */}
        <div className="mb-14 lg:mb-20 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="block h-px w-8 bg-[#D4A574]" aria-hidden="true" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#D4A574] font-semibold">
              Core Revenue Pillars
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B1528] leading-[1.2] mb-5">
            Hands-On Revenue Management for Independent &amp; Boutique Hotels.
          </h1>
          
          <p className="text-base md:text-lg text-slate-600 leading-relaxed font-sans">
            Independent hotels lose 12% to 20% of top-line revenue to hidden OTA discounting and static rates. Here is the hands-on operational model I run to protect your room rates and grow take-home profit.
          </p>
        </div>

        {/* ========================================================================= */}
        {/* ROW 1: 7/5 Asymmetric Split (Methodology & Deliverables + Preview Badge) */}
        {/* ========================================================================= */}
        <div ref={row1Ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch mb-16 lg:mb-24 transition-all duration-700 ease-out ${row1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* 7 Columns: Pillar 01 & Pillar 02 */}
          <div className="lg:col-span-7 flex flex-col justify-between p-8 md:p-10 rounded-2xl bg-white border border-slate-200 shadow-sm hover:border-[#D4A574]/60 transition-all duration-300 group">
            <div>
              <div className="flex items-center justify-between gap-4 pb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 rounded-md bg-[#D4A574]/15 text-[#8C6228] font-mono text-xs font-bold uppercase tracking-wider">
                    Phase 01 &bull; Rates &amp; Distribution
                  </span>
                  <span className="text-xs font-mono text-slate-400">Pillars 01-02</span>
                </div>
                <div className="size-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:text-[#D4A574] group-hover:bg-[#D4A574]/10 transition-colors">
                  <Sliders size={20} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                {/* Pillar 01 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-[#0B1528] text-[#D4A574] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <Briefcase size={20} />
                    </div>
                    <div>
                      <span className="font-mono text-xs text-[#D4A574] font-semibold">01 / DISTRIBUTION</span>
                      <h2 className="text-lg font-bold text-[#0B1528] leading-snug">OTA Extranet Management &amp; Rate Parity Protection</h2>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed pt-1">
                    Online Travel Agencies often apply localized markdowns or cashbacks that sell your rooms cheaper than your front desk. I audit your listings daily on Booking.com, Agoda, MakeMyTrip, and Expedia to stop unauthorized undercutting and maintain strict channel parity. I also ensure your{" "}
                    <Link href="https://hotelrevbaba.com/optimizing-ota-channel-distribution-a-datadriven-checklist-for-hotel-revenue-managers/" className="text-[#8C6228] underline underline-offset-4 decoration-[#D4A574]/60 hover:text-[#0B1528] hover:decoration-[#0B1528] font-medium transition-colors">
                      rates are protected
                    </Link>{" "}
                    across every channel. Every week, I review which channels justify their commission, which is central to{" "}
                    <Link href="https://hotelrevbaba.com/optimizing-ota-channel-mix-for-maximum-hotel-revenue/" className="text-[#8C6228] underline underline-offset-4 decoration-[#D4A574]/60 hover:text-[#0B1528] hover:decoration-[#0B1528] font-medium transition-colors">
                      optimizing OTA channel mix
                    </Link>{" "}
                    and driving repeat guests through direct booking strategies for hotels.
                  </p>
                  <ul className="space-y-1.5 pt-2 text-xs font-medium text-slate-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#D4A574] shrink-0" />
                      <span>Daily dynamic pricing against competitor compsets</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#D4A574] shrink-0" />
                      <span>15–25% net revenue improvement</span>
                    </li>
                  </ul>
                </div>

                {/* Pillar 02 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-[#0B1528] text-[#D4A574] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <Zap size={20} />
                    </div>
                    <div>
                      <span className="font-mono text-xs text-[#D4A574] font-semibold">02 / CONNECTIVITY</span>
                      <h2 className="text-lg font-bold text-[#0B1528] leading-snug">Channel Manager &amp; PMS Stack Auditing</h2>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed pt-1">
                    Two-way inventory syncing between your PMS and channel manager must be seamless. We audit room category mappings, rate plans, and minimum stay rules to eliminate accidental overbookings and unsold inventory. Practical{" "}
                    <Link href="https://hotelrevbaba.com/hotel-revenue-management-technology-stack-pms-rms-and-ota-integration/" className="text-[#8C6228] underline underline-offset-4 decoration-[#D4A574]/60 hover:text-[#0B1528] hover:decoration-[#0B1528] font-medium transition-colors">
                      PMS and RMS integration
                    </Link>{" "}
                    protects live inventory and stops rate leakage across every shift.
                  </p>
                  <ul className="space-y-1.5 pt-2 text-xs font-medium text-slate-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#D4A574] shrink-0" />
                      <span>Two-way PMS and channel manager mapping</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#D4A574] shrink-0" />
                      <span>Fixing rate leaks and overbooking risks</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-xs font-mono text-slate-500">
                <span className="size-2 rounded-lg bg-emerald-500 animate-pulse" />
                Active Distribution &amp; Rate Monitoring
              </div>
              <Link href="https://hotelrevbaba.com/the-ultimate-ota-listing-optimization-guide-how-small-hotels-with-30-rooms-or-fewer-can-boost-online-bookings/" className="text-xs font-semibold text-[#0B1528] hover:text-[#8C6228] underline decoration-[#D4A574]/60 underline-offset-4 inline-flex items-center gap-1.5 transition-colors">
                Read our OTA listing optimization guide <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          {/* 5 Columns: Interactive Yield Simulator Preview */}
          <div className="lg:col-span-5 rounded-2xl bg-[#0B1528] p-8 text-white border border-slate-800 shadow-md flex flex-col justify-between relative overflow-hidden">
            <div aria-hidden="true" className="absolute -right-16 -top-16 size-48 rounded-lg bg-[#D4A574]/15 blur-3xl pointer-events-none" />
            
            <div>
              <div className="flex items-center justify-between pb-5 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <span className="size-2 rounded-lg bg-[#D4A574]" />
                  <span className="font-mono text-xs uppercase tracking-widest text-[#D4A574]">Example View &bull; Rate Matrix</span>
                </div>
                <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-white/10 text-white/80">
                  Example Compset Index: 118.4
                </span>
              </div>

              <div className="mt-6 space-y-4">
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex justify-between items-center text-xs text-white/70 mb-2">
                    <span>Example Recommended ADR</span>
                    <span className="text-emerald-400 font-mono font-bold">+18.6%</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold font-serif text-white">₹7,450</span>
                    <span className="text-xs text-white/50 line-through">₹6,280</span>
                    <span className="ml-auto text-[11px] font-mono text-[#D4A574] bg-[#D4A574]/15 px-2 py-0.5 rounded">
                      Demand Surge
                    </span>
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <div className="flex justify-between text-xs text-white/80">
                    <span>Direct vs OTA Channel Share (Illustrative)</span>
                    <span className="font-mono text-[#D4A574]">38% Direct / 62% OTA</span>
                  </div>
                  <div className="w-full h-2 rounded-lg bg-white/10 overflow-hidden flex">
                    <div className="bg-[#D4A574] h-full" style={{
                    width: '38%'
                  }} />
                    <div className="bg-slate-500 h-full" style={{
                    width: '62%'
                  }} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="p-3 rounded-lg bg-white/5 border border-white/5">
                    <span className="text-[10px] uppercase font-mono text-white/50 block">RevPAR Forecast</span>
                    <span className="text-lg font-bold text-white font-serif">₹5,820</span>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5 border border-white/5">
                    <span className="text-[10px] uppercase font-mono text-white/50 block">Target Occupancy</span>
                    <span className="text-lg font-bold text-emerald-400 font-serif">84.2%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-5 border-t border-white/10 flex items-center justify-between">
              <span className="text-xs text-white/60">Illustrative Rate Parity &amp; Forecasting Model</span>
              <Link to="/RevenueCalculatorPage" className="text-xs font-semibold text-[#D4A574] hover:text-white flex items-center gap-1">
                Calculate Potential Gains <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* ROW 2: Reversed 5/7 Asymmetric Split (Imagery/KPI + Pillar Breakdown) */}
        {/* ========================================================================= */}
        <div ref={row2Ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch transition-all duration-700 ease-out ${row2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* 5 Columns: Diagnostic Card with Metric Overlay */}
          <div className="lg:col-span-5 rounded-2xl bg-white border border-slate-200 p-8 shadow-sm hover:border-[#D4A574]/60 transition-all duration-300 flex flex-col justify-between group overflow-hidden relative">
            <div>
              <div className="flex items-center justify-between pb-5 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="size-2 rounded-lg bg-[#D4A574]" />
                  <span className="font-mono text-xs uppercase tracking-widest text-[#8C6228] font-bold">
                    Extranet Diagnostics
                  </span>
                </div>
                <span className="text-xs font-mono text-slate-500">Pillars 03-04</span>
              </div>

              {/* Visual Graphic Representation */}
              <div className="mt-6 relative rounded-xl overflow-hidden bg-slate-900 aspect-video md:aspect-[4/3] flex items-end p-5">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_analytics_dashboard.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_analytics_dashboard.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_analytics_dashboard.png-wvc-sizes" alt="Hotel revenue tracking and extranet audit dashboard" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1528] via-[#0B1528]/60 to-transparent" />
                
                <div className="relative z-10 w-full">
                  <div className="inline-block px-2.5 py-1 rounded bg-[#D4A574] text-[#0B1528] text-[11px] font-bold uppercase tracking-wider mb-2 font-mono">
                    Audit Sample
                  </div>
                  <h3 className="text-white text-lg font-bold font-sans">
                    Typical audit finding: 10–15% of net profit missed
                  </h3>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div className="text-[11px] font-mono text-slate-500 uppercase">Rate Leaks Found</div>
                  <div className="text-base font-bold text-[#0B1528] font-serif">₹4.8L / Mo Avg</div>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div className="text-[11px] font-mono text-slate-500 uppercase">Audit Turnaround</div>
                  <div className="text-base font-bold text-[#0B1528] font-serif">14-Day Review</div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
              <span>Daily Compset &amp; Parity Checks</span>
              <span className="text-[#8C6228] font-semibold">Tracked Daily</span>
            </div>
          </div>

          {/* 7 Columns: Pillar 03 & Pillar 04 */}
          <div className="lg:col-span-7 flex flex-col justify-between p-8 md:p-10 rounded-2xl bg-white border border-slate-200 shadow-sm hover:border-[#D4A574]/60 transition-all duration-300 group">
            <div>
              <div className="flex items-center justify-between gap-4 pb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 rounded-md bg-[#D4A574]/15 text-[#8C6228] font-mono text-xs font-bold uppercase tracking-wider">
                    Phase 02 &bull; Audits &amp; Daily Pricing
                  </span>
                  <span className="text-xs font-mono text-slate-400">Continuous Growth</span>
                </div>
                <div className="size-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:text-[#D4A574] group-hover:bg-[#D4A574]/10 transition-colors">
                  <TrendingUp size={20} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                {/* Pillar 03 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-[#0B1528] text-[#D4A574] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <BarChart3 size={20} />
                    </div>
                    <div>
                      <span className="font-mono text-xs text-[#D4A574] font-semibold">03 / AUDITS</span>
                      <h2 className="text-lg font-bold text-[#0B1528] leading-snug">Revenue &amp; Extranet Audits</h2>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed pt-1">
                    I audit room categorization, cancellation slippage, discount stacking, and OTA commission tiers (18–22%) to stop{" "}
                    <Link href="https://hotelrevbaba.com/case-study-how-sunil-singh-increased-a-midscale-hotels-ota-revenue-by-22/" className="text-[#8C6228] underline underline-offset-4 decoration-[#D4A574]/60 hover:text-[#0B1528] hover:decoration-[#0B1528] font-medium transition-colors">
                      OTA distribution leakage
                    </Link>{" "}
                    and protect your take-home margins. Most hotels leak 12% to 20% of revenue here without knowing it.
                  </p>
                  <ul className="space-y-1.5 pt-2 text-xs font-medium text-slate-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#D4A574] shrink-0" />
                      <span>Extranet audit across all active channels</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#D4A574] shrink-0" />
                      <span>Negotiating commission tiers and stopping undercutting</span>
                    </li>
                  </ul>
                </div>

                {/* Pillar 04 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-[#0B1528] text-[#D4A574] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <Flame size={20} />
                    </div>
                    <div>
                      <span className="font-mono text-xs text-[#D4A574] font-semibold">04 / PRICING</span>
                      <h2 className="text-lg font-bold text-[#0B1528] leading-snug">Dynamic Pricing &amp; Yield Management</h2>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed pt-1">
                    Leaving weekend rates flat until 48 hours before check-in costs you margin. We track real-time market compression and booking velocity to increase Average Daily Rate (ADR) during demand surges and protect baseline occupancy during off-peak dates.
                  </p>
                  <ul className="space-y-1.5 pt-2 text-xs font-medium text-slate-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#D4A574] shrink-0" />
                      <span>30-60-90 day pickup pace and demand outlook</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#D4A574] shrink-0" />
                      <span>MLOS controls to protect peak weekend rates</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Layers size={16} className="text-[#D4A574]" />
                <span className="text-xs text-slate-600 font-medium">Ready to see where your hotel is leaking revenue?</span>
              </div>
              <Button asChild className="bg-[#D4A574] hover:bg-[#c29362] text-[#0B1528] font-bold text-xs uppercase tracking-wider rounded-lg px-5 py-2.5 h-auto transition-all shadow-sm">
                <Link to="/ContactPage#start-your-revenue-journey">
                  Book a Free Revenue Audit
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* Bottom Statistics / Confidence Row */}
        {/* ========================================================================= */}
        <div className="mt-16 md:mt-24 pt-12 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, i) => <div key={i} data-index={i} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-xs hover:border-[#D4A574]/40 transition-colors">
              <div className="flex items-baseline gap-1.5 mb-2">
                <span className="font-serif text-3xl md:text-4xl font-bold text-[#0B1528]">
                  {stat.number}
                </span>
                {stat.unit && <span className="text-xs font-mono font-semibold text-[#D4A574] uppercase">
                    {stat.unit}
                  </span>}
              </div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500">
                {stat.label}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}